import {TextTKey} from '../../../../../../common/type';
import {ViewerGuideType} from '../../../../../../design-system/organisms/ViewerGuide/ViewerGuideType.enum';
import {ViewerSettingFontType} from '../../../../../../design-system/organisms/ViewerSetting/ViewerSettingFontItemType.interface';
import {ViewerSettingThemeType} from '../../../../../../design-system/organisms/ViewerSetting/ViewerSettingThemeItemType.interface';
import {SlideType} from '../../../../../../graphql/generated/types';
import {PageviewLogViewerName} from '../type/PageviewLogViewerName.enum';

export const VIEWER_END_STATE_NAME = 'viewerEnd';
export const VIEWER_INFO_STATE_NAME = 'viewerInfo';
export const VIEWER_MENU_STATE_NAME = 'viewerMenu';
export const IMAGE_VIEWER_STATE_NAME = 'imageViewer';
export const TEXT_VIEWER_STATE_NAME = 'textViewer';

/**
 * 뷰어 설정의 종류는 TextViewer, ImageViewer, TalkViewer에만 존재합니다. 오디오 뷰어는 웹에 없고, 영상 뷰어는 뷰어 설정이 없습니다.
 */
export enum ViewerType {
  TextViewer = 'TextViewer',
  ImageViewer = 'ImageViewer',
  TalkViewer = 'TalkViewer',
  VideoViewer = 'VideoViewer',
  None = 'None',
}

export function viewerTypeToLocalStorageKey(viewerType: ViewerType) {
  switch (viewerType) {
    case ViewerType.TextViewer:
      return 'text_viewer';
    case ViewerType.TalkViewer:
      return 'talk_viewer';
    case ViewerType.ImageViewer:
      return 'image_viewer';
  }
}

//열람 방식
export const LOCAL_STORAGE_VIEWER_SETTING_READ_TYPE_KEY =
  'viewer_setting_read_type';
//테마
export const LOCAL_STORAGE_VIEWER_SETTING_THEME_KEY = 'viewer_setting_theme';
//글꼴
export const LOCAL_STORAGE_VIEWER_SETTING_FONT_FAMILY_KEY =
  'viewer_setting_font_family';
//글자 크기
export const LOCAL_STORAGE_VIEWER_SETTING_FONT_SIZE_KEY =
  'viewer_setting_font_size';
//줄 간격
export const LOCAL_STORAGE_VIEWER_SETTING_LINE_GAP_KEY =
  'viewer_setting_line_gap';
//여백
export const LOCAL_STORAGE_VIEWER_SETTING_CONTENT_MARGIN_KEY =
  'viewer_setting_content_margin';
//여백 색
export const LOCAL_STORAGE_VIEWER_SETTING_SPACE_COLOR_KEY =
  'viewer_setting_space_color';
//두쪽 보기
export const LOCAL_STORAGE_VIEWER_SETTING_IS_TWO_PAGE_LAYOUT =
  'viewer_setting_is_two_page_layout';

//뷰어 툴팁 닫기 여부
export const LOCAL_STORAGE_VIEWER_TOOLTIP_IS_CLOSED =
  'viewer_tooltip_is_closed';

//뷰어 미리보기 댓글 on/off 여부
export const LOCAL_STORAGE_VIEWER_IS_PREVIEW_COMMENT_ON =
  'viewer_is_preview_comment_on';

//뷰어 진입 후 뷰어 메뉴가 닫히는 시간(ms)
export const VIEWER_MENU_FIRST_EXPOSE_TIME = 2000;

export const SPACE_COLOR_DEFAULT = 'White';
export const IS_TWO_PAGE_LAYOUT_DEFAULT = true;
export const THEME_DEFAULT = 'White';
export const FONT_FAMILY_DEFAULT = 'Batang';
export const FONT_SIZE_MIN = 1;
export const FONT_SIZE_MAX = 9;
export const FONT_SIZE_DEFAULT = 4;
export const LINE_GAP_MIN = 1;
export const LINE_GAP_MAX = 5;
export const LINE_GAP_DEFAULT = 2;
export const CONTENT_MARGIN_MIN = 1;
export const CONTENT_MARGIN_MAX = 5;
export const CONTENT_MARGIN_DEFAULT = 1;

export const VIEWER_TOAST_MESSAGES: {
  [key: string]: TextTKey;
} = {
  likeOn: {
    text: '좋아하는 작품으로 설정했습니다.\n작품이 업데이트 되면 알림을 드립니다.',
    textTKey: 'common_fav_notification_toast',
  },
  likeOff: {
    text: '좋아요를 취소했습니다.\n작품 업데이트 알림을 더 이상 받을 수 없습니다.',
    textTKey: 'common_fav_undo_toast',
  },
  likeAlready: {
    text: '이미 좋아하는 작품으로 설정되어 있습니다.',
    // @ts-expect-error
    textTKey: '',
  },
  alarmOn: {
    text: '작품 알림이 설정되었습니다.',
    textTKey: 'common_notification_on_toast',
  },
  alarmOff: {
    text: '작품 알림이 해제 되었습니다.',
    textTKey: 'common_notification_undo_toast',
  },
  ratingConfirm: {
    text: '별점이 입력되었습니다.',
    textTKey: 'viewer_end_rating_toast_1',
  },
  ratingModify: {
    text: '별점이 수정되었습니다.',
    textTKey: 'viewer_end_rating_toast_2',
  },
  nonStopWatchingOn: {
    text: '정주행 모드로 전환되었습니다.',
    textTKey: 'viewer_binge_watch_start_toast',
  },
  nonStopWatchingOff: {
    text: '정주행 모드가 종료되었습니다.',
    textTKey: 'viewer_binge_watch_end_toast',
  },
  talkViewerEnd: {
    // TODO : 톡뷰어 v2 디자인으로 변경 작업 진행하며 넣어야 할 것 같습니다.
    text: '회차의 마지막입니다.',
    // @ts-expect-error
    textTKey: '',
  },
};

export const SCROLL_TOP_IMAGE_VIEWER_EVENT_NAME = 'scrolltopimageviewer';
export const SLIDE_TO_ZERO_IMAGE_VIEWER_EVENT_NAME = 'slidetozeroimageviewer';
export const SLIDE_TO_ZERO_TEXT_VIEWER_EVENT_NAME = 'slidetozerotextviewer';

export const SLIDE_TYPE_TO_PAGE_LOG_VIEWER_TYPE_MAP: {
  [key in SlideType]: PageviewLogViewerName | undefined;
} = {
  [SlideType.Epub]: PageviewLogViewerName.TextViewer,
  [SlideType.Comic]: PageviewLogViewerName.ImageViewer,
  [SlideType.TalkNovel]: PageviewLogViewerName.TalkViewer,
  [SlideType.TalkHumor]: PageviewLogViewerName.VideoViewer,
  [SlideType.Slide]: undefined,
  [SlideType.AudioBook]: undefined,
  [SlideType.Vod]: undefined,
  [SlideType.VideoOnly]: undefined,
  [SlideType.None]: undefined,
};

export const VIEWER_GUIDE_TO_PAGE_LOG_VIEWING_MODE_MAP: {
  [key in ViewerGuideType]: 'page' | 'scroll' | 'tap' | undefined;
} = {
  [ViewerGuideType.Forward]: 'page',
  [ViewerGuideType.Reverse]: 'page',
  [ViewerGuideType.Scroll]: 'scroll',
  [ViewerGuideType.Tap]: 'tap',
};

export const CONTENTS_MOBILE_MAX_WIDTH = 600;
export const CONTENTS_PC_MAX_WIDTH = 720;
export const CONTENTS_PC_MAX_HEIGHT = 920;
export const CONTENTS_PC_TWO_PAGE_MAX_WIDTH = 720 * 2;
export const PAGE_TEXT_VIEWER_CONTENTS_PC_MIN_WIDTH = 523;
export const PAGE_TEXT_VIEWER_CONTENTS_PC_MAX_WIDTH = '45%';
export const PAGE_TEXT_VIEWER_CONTENTS_PC_TWO_PAGE_MAX_WIDTH = '65%';
export const CONTENTS_PC_MARGIN_TOP_BOTTOM = 60;

export const TEXT_VIEWER_DARK_THEME_LIST = ['DarkGray', 'Black', 'Green'];

export const VIEWER_SETTING_THEME_TYPE_TO_COPY_MAP_FOR_LOGGING: {
  [key in ViewerSettingThemeType]: string;
} = {
  White: '화이트',
  LightGray: '라이트그레이',
  DarkGray: '다크그레이',
  Black: '블랙',
  Sepia: '베이지',
  Green: '그린',
};

export const VIEWER_SETTING_FONT_TYPE_TO_COPY_MAP_FOR_LOGGING: {
  [key in ViewerSettingFontType]: string;
} = {
  Gothic: '고딕',
  Batang: '바탕',
  Myungjo: '명조',
};

// 최대 Rate 수치입니다. 끝까지 다 보면 10000입니다
export const MAX_PROGRESS_RATE = 10000;

// 시크바 관련
export const MOBILE_VIEWER_SEEKBAR_HEIGHT_PX = 25;
export const PC_VIDEO_VIEWER_SEEKBAR_HEIGHT_PX = 58;
export const PROGRESS_BAR_HEIGHT_PX = 2;
